import { memo, useCallback, useState } from 'react'

import { TextField, Paper, Typography, Box, Switch, Alert } from '@mui/material'
import { SmepEvent, SmepEventEdit, SmepEventInsert } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { Controller, useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  selectSubmissionProgressSetters,
  SubmissionButton,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { CLEARED_TIME, isClearedTime, isEndNotAfterStart } from 'domains/Workspaces/Events/schedule'
import { ScheduleRangeFields } from 'domains/Workspaces/Events/ScheduleRangeFields'
import { useEventCreateMutation, useEventUpdateMutation } from 'hooks/mutations/smep'
import { eventsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { workspaceQueryKeys } from 'services/queryKeys'

/**
 * Same schedule convention as the config editor's form: `manualStartTime` is an
 * ISO string (empty when unset) and `manualEndTime === null` means open end. The
 * SDK's `DateTime.MinValue` sentinel only appears in the payloads below.
 */
export type FormStructure = {
  name?: string | null
  manualStartTime?: string
  manualEndTime?: string | null
}

const scheduleDefaults = (event?: SmepEvent): Pick<FormStructure, 'manualStartTime' | 'manualEndTime'> => ({
  manualStartTime: isClearedTime(event?.manualStartTime) ? '' : event?.manualStartTime,
  manualEndTime: isClearedTime(event?.manualEndTime) ? null : event?.manualEndTime,
})

type NewDialogProps = {
  onSuccess?: (data: SmepEvent) => void
  initialValue?: SmepEvent
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const handleClose = useCloseHandler()

  const { workspaceId } = eventsRoute.useParams()

  const workspaceHasDefaultConfigQuery = useQuery({
    ...workspaceQueryKeys.detail({ workspaceId }),
    select: (data) => data.defaultEventConfig?.enabled === true && data.defaultEventConfig?.config != null,
  })

  const [billable, setBillable] = useState(initialValue ? !initialValue.nonBillable : true)

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { handleSubmit, control, getValues, trigger } = useForm<FormStructure>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: initialValue?.name,
      ...scheduleDefaults(initialValue),
    },
  })

  const startController = useController({
    control,
    name: 'manualStartTime',
    rules: {
      validate: (value) => !!value || t('domain:Workspace.Event.startTimeRequired'),
    },
  })
  const endController = useController({
    control,
    name: 'manualEndTime',
    rules: {
      validate: (value) =>
        !isEndNotAfterStart(getValues('manualStartTime'), value) || t('domain:Workspace.Event.endBeforeStart'),
    },
  })

  const { mutate: createEvent, ...createMutation } = useEventCreateMutation({})
  const { mutate: updateEvent, ...updateMutation } = useEventUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      // Validation guarantees a start time; an open end means no end at all — an insert
      // simply omits it, while an edit has to send the sentinel to clear a previous one.
      const { manualStartTime } = data
      const manualEndTime = data.manualEndTime ?? undefined

      if (!initialValue) {
        const smepEventInsert = {
          workspaceId,
          name: data.name,
          manualStartTime,
          manualEndTime,
          nonBillable: !billable,
        } satisfies SmepEventInsert

        createEvent(
          { smepEventInsert },
          {
            onSuccess: (data) => {
              setTimeout(() => {
                handleClose()

                if (onSuccess) {
                  setTimeout(() => {
                    onSuccess(data)
                  }, 300)
                }
              }, 700)
            },
            onError: (_, variables) => {
              setCurrentStep({
                value: 100,
                description: `${variables.smepEventInsert.name} could not be created`,
              })
            },
          },
        )
      } else {
        const smepEventEdit = {
          name: data.name,
          manualStartTime,
          manualEndTime: manualEndTime ?? CLEARED_TIME,
          nonBillable: !billable,
        } satisfies SmepEventEdit

        updateEvent(
          { eventId: initialValue.id, smepEventEdit },
          {
            onSuccess: (data) => {
              setTimeout(() => {
                handleClose()

                if (onSuccess) {
                  setTimeout(() => {
                    onSuccess(data)
                  }, 300)
                }
              }, 700)
            },
            onError: (_, variables) => {
              setCurrentStep({
                value: 100,
                description: `${variables.smepEventEdit.name} was not updated`,
              })
            },
          },
        )
      }
    },
    [billable, createEvent, handleClose, initialValue, onSuccess, setCurrentStep, updateEvent, workspaceId],
  )

  return (
    <DialogFormContent
      title={t('domain:Workspace.Event.createEvent')}
      onSubmit={handleSubmit(handleFormSubmit)}
      description={t('domain:Workspace.Event.createEventDescription')}
      SubmitProgressZone={
        <SubmissionProgress
          isSuccess={createMutation.isSuccess || updateMutation.isSuccess}
          isError={createMutation.isError || updateMutation.isError}
        />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!createMutation.isError || updateMutation.isError}
          successCondition={createMutation.isSuccess || updateMutation.isSuccess}
          errorCondition={createMutation.isError || updateMutation.isError}
          loading={createMutation.isPending || updateMutation.isPending}
          disabled={
            createMutation.isPending || createMutation.isSuccess || updateMutation.isPending || updateMutation.isSuccess
          }
        />
      }
    >
      {!initialValue && workspaceHasDefaultConfigQuery.data && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('domain:Workspace.Event.createEventWithDefaultConfig')}
        </Alert>
      )}
      <Controller
        name="name"
        control={control}
        rules={{
          minLength: 3,
        }}
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            sx={{ flex: 1 }}
            {...field}
            value={field.value ?? ''}
            required
            margin="dense"
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
            autoComplete="off"
            error={invalid}
            helperText={error?.message}
          />
        )}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 1, mt: 3 }}>
        <Paper sx={{ width: '100%', bgcolor: 'background.levels.900', p: 1, mx: -1 }}>
          <Typography variant="heading-base">{t('domain:Workspace.Event.schedule')}</Typography>
          <Box sx={{ mt: 2 }}>
            <ScheduleRangeFields
              startTime={startController.field.value}
              endTime={endController.field.value}
              startError={startController.fieldState.error?.message}
              onStartTimeChange={(value) => {
                startController.field.onChange(value)
                // Re-validate both sides so the "required"/"before start" errors clear live.
                void trigger(['manualStartTime', 'manualEndTime'])
              }}
              onEndTimeChange={endController.field.onChange}
              endError={endController.fieldState.error?.message}
            />
          </Box>
        </Paper>
        <Paper sx={{ width: '100%', bgcolor: 'background.levels.900', p: 1, mx: -1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={[!billable && { color: 'action.disabled' }]}>
              <Typography variant="heading-base">{t('domain:Workspace.Event.billable')}</Typography>
              <Typography variant="text">{t('domain:Workspace.Event.billableDescription')}</Typography>
            </Box>
            <Switch checked={billable} onChange={(_, value) => setBillable(value)} />
          </Box>
        </Paper>
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
